import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { Row } from '@tanstack/react-table';
import type { Equipment } from 'contracts/temperature-control/equipments';
import { notify, eventBus, type EventBusRegistry } from 'services';
import { usePermission } from 'hooks/usePermission';
import {
  useFetchEquipments,
  useCreateEquipment,
  useUpdateEquipment,
  useDeleteEquipment,
} from 'hooks/api/temperature-control/equipment';
import { useEquipmentSearchParams } from 'hooks/store/temperature-control/use-equipment-search-params';
import { Scaffold } from 'layouts';
import { DataTableV2 } from 'components/ui/DataTableV2';
import { Button } from 'components/ui/button';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';

import { useEquipmentColumns } from './hooks';
import {
  EquipmentFilters,
  CreateEquipmentDrawer,
  type CreateEquipmentDrawerRef,
  UpdateEquipmentDrawer,
  type UpdateEquipmentDrawerRef,
} from './components';
import { TabbedNavigation } from '../shared-components';

const QUERY_KEYS = {
  FETCH_EQUIPMENTS: 'fetch-equipments',
};

export const ListEquipmentsPage = () => {
  const { hasPermissionTo } = usePermission();
  const {
    filters,
    updateFilters,
    paginationControl,
    updatePaginationControl,
    clearState,
  } = useEquipmentSearchParams();
  const { fetchEquipments } = useFetchEquipments();
  const { createEquipment } = useCreateEquipment();
  const { updateEquipment } = useUpdateEquipment();
  const { deleteEquipment } = useDeleteEquipment();

  const queryClient = useQueryClient();
  const columns = useEquipmentColumns();
  const createEquipmentDrawerRef = React.useRef<CreateEquipmentDrawerRef>(null);
  const updateEquipmentDrawerRef = React.useRef<UpdateEquipmentDrawerRef>(null);
  const deleteEquipmentConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);

  const equipmentsQuery = useQuery({
    queryKey: [QUERY_KEYS.FETCH_EQUIPMENTS, filters, paginationControl],
    queryFn: () => fetchEquipments({
      filters,
      pagination: paginationControl,
    }),
  });

  const createEquipmentMutation = useMutation({
    mutationKey: ['create-equipment'],
    mutationFn: createEquipment,
    onSuccess: async () => {
      notify('success', 'Equipamento cadastrado com sucesso!');
      await queryClient.invalidateQueries([QUERY_KEYS.FETCH_EQUIPMENTS]);
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
    onSettled: () => {
      createEquipmentDrawerRef.current?.close();
    },
  });

  const updateEquipmentMutation = useMutation({
    mutationKey: ['update-equipment'],
    mutationFn: updateEquipment,
    onSuccess: async () => {
      notify('success', 'Equipamento atualizado com sucesso!');
      await queryClient.invalidateQueries([QUERY_KEYS.FETCH_EQUIPMENTS]);
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
    onSettled: () => {
      updateEquipmentDrawerRef.current?.close();
    },
  });

  const deleteEquipmentMutation = useMutation({
    mutationKey: ['delete-equipment'],
    mutationFn: deleteEquipment,
    onSuccess: async () => {
      notify('success', 'Equipamento removido com sucesso!');
      await queryClient.invalidateQueries([QUERY_KEYS.FETCH_EQUIPMENTS]);
    },
    onError: (error: any) => {
      notify('error', error?.message);
    },
  });

  const openDeleteEquipmentConfirmationDialog = React.useCallback(async (equipment: Equipment) => {
    const res = await deleteEquipmentConfirmationDialogRef.current?.openDialog({
      title: 'Remover equipamento',
      message: (
        <div>
          Confirmar remoção do equipamento de código{' '}
          <span className={'font-GilroySemibold'}>{equipment.equipmentCode}</span>?
        </div>
      ),
    });

    if (res === 'confirmed') {
      deleteEquipmentMutation.mutate(equipment.id);
    }
  }, []);

  React.useEffect(() => {
    const registries: EventBusRegistry[] = [];
    registries.push(eventBus.subscribe(
      'show-delete-equipment-confirmation-dialog',
      openDeleteEquipmentConfirmationDialog,
    ));

    return () => {
      registries.forEach(registry => registry.unsubscribe());
    };
  }, [openDeleteEquipmentConfirmationDialog]);

  return (
    <Scaffold>
      <TabbedNavigation />
      <PageHeaderForFilters className={'mb-6'}
       headerTitle={<span className={'font-GilroySemibold'}>Filtros</span>}
      >
        <EquipmentFilters
          onSubmitFilters={updateFilters}
          onResetFilters={clearState}
        />
      </PageHeaderForFilters>
      <CreateEquipmentDrawer
        ref={createEquipmentDrawerRef}
        onSubmit={createEquipmentMutation.mutate}
        loading={createEquipmentMutation.isLoading}
      />
      <UpdateEquipmentDrawer
        ref={updateEquipmentDrawerRef}
        onSubmit={updateEquipmentMutation.mutate}
        loading={updateEquipmentMutation.isLoading}
      />
      <ConfirmationDialog
        ref={deleteEquipmentConfirmationDialogRef}
      />
      <DataTableV2
        columns={columns}
        data={equipmentsQuery.data?.data || []}
        isLoading={equipmentsQuery.isLoading}
        manualPagination
        onPaginationChange={updatePaginationControl}
        onSort={updatePaginationControl}
        onRowClick={(row: Row<Equipment>) => {
          if (hasPermissionTo(['TEMPERATURE_CONTROL.EQUIPMENT.UPDATE'])) {
            updateEquipmentDrawerRef.current?.open(row.original);
          }
        }}
        pagination={{
          ...equipmentsQuery?.data?.pagination,
          currentPage: paginationControl.page,
          perPage: paginationControl?.limit,
        }}
        renderTopBar={() => (
          <div className={'flex w-full justify-end'}>
            {hasPermissionTo(['TEMPERATURE_CONTROL.EQUIPMENT.CREATE']) && (
              <Button onClick={createEquipmentDrawerRef.current?.open}>Novo equipamento</Button>
            )}
          </div>
        )}
      />
    </Scaffold>
  );
};